<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- Overlay -->
      <div 
        class="fixed inset-0 transition-opacity bg-black bg-opacity-75"
        @click="$emit('close')"
      ></div>

      <!-- Modal -->
      <div class="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-discord-dark-300 shadow-xl rounded-lg">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-semibold text-discord-light-100">
            {{ t('uploadCrop.cropTitle') }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-discord-light-600 hover:text-discord-light-100 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Crop Area -->
        <div class="space-y-4">
          <div class="text-center mb-4">
            <p class="text-sm text-discord-light-600">{{ t('uploadCrop.cropHint') }}</p>
          </div>
          
          <div class="crop-wrapper">
            <Cropper
              ref="cropper"
              :src="imageUrl"
              :stencil-props="{
                aspectRatio: 1,
                movable: true,
                resizable: true
              }"
              :default-size="{
                width: 200,
                height: 200
              }"
              :transitions="true"
              :auto-zoom="true"
              class="cropper"
              @change="onChange"
            />
          </div>
          
          <!-- Controls -->
          <div class="flex justify-center space-x-4">
            <button
              @click="rotateLeft"
              class="px-4 py-2 bg-discord-dark-200 hover:bg-discord-dark-100 text-discord-light-100 rounded-lg transition-colors"
              :title="t('uploadCrop.rotateLeft')"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3v5h5"/>
              </svg>
            </button>
            
            <button
              @click="rotateRight"
              class="px-4 py-2 bg-discord-dark-200 hover:bg-discord-dark-100 text-discord-light-100 rounded-lg transition-colors"
              :title="t('uploadCrop.rotateRight')"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 3v5h-5"/>
              </svg>
            </button>
            
            <button
              @click="resetCrop"
              class="px-4 py-2 bg-discord-dark-200 hover:bg-discord-dark-100 text-discord-light-100 rounded-lg transition-colors"
              :title="t('uploadCrop.center')"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-discord-dark-200">
          <button
            @click="$emit('close')"
            class="px-6 py-2 bg-discord-dark-200 hover:bg-discord-dark-100 text-discord-light-100 rounded-lg transition-colors"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="cropImage"
            class="px-6 py-2 bg-discord-blurple hover:bg-discord-blurple-dark text-white rounded-lg transition-colors"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ t('uploadCrop.processing') }}
            </span>
            <span v-else>{{ t('uploadCrop.confirm') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

const { t } = useI18n()

const props = defineProps({
  imageUrl: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'cropped'])

// Refs
const cropper = ref(null)

// State
const isProcessing = ref(false)

const onChange = ({ coordinates, canvas }) => {
  // Handle crop changes if needed
}

const rotateLeft = () => {
  if (cropper.value) {
    cropper.value.rotate(-90)
  }
}

const rotateRight = () => {
  if (cropper.value) {
    cropper.value.rotate(90)
  }
}

const resetCrop = () => {
  if (cropper.value) {
    cropper.value.reset()
  }
}

const cropImage = async () => {
  if (!cropper.value) return

  isProcessing.value = true

  try {
    const { canvas } = cropper.value.getResult()
    
    if (canvas) {
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.8)
      emit('cropped', croppedDataUrl)
    }
    
  } catch (error) {
    console.error('Erro ao fazer crop da imagem:', error)
    alert(t('uploadCrop.cropError'))
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
.crop-wrapper {
  position: relative;
  height: 500px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}

.cropper {
  height: 100%;
  background: #1a1a1a;
}

/* Vue Advanced Cropper custom styles */
:deep(.vue-advanced-cropper__background) {
  background: #1a1a1a;
}

:deep(.vue-advanced-cropper__foreground) {
  border-radius: 8px;
}

:deep(.vue-advanced-cropper__area) {
  background: rgba(0, 0, 0, 0.3);
}

:deep(.vue-advanced-cropper__stencil) {
  border: 2px solid #5865F2;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3);
}

:deep(.vue-advanced-cropper__handler) {
  background: #5865F2;
  border: 2px solid #fff;
  border-radius: 50%;
  width: 8px;
  height: 8px;
}

:deep(.vue-advanced-cropper__handler--corner) {
  width: 12px;
  height: 12px;
}

:deep(.vue-advanced-cropper__line) {
  background: #5865F2;
  opacity: 0.3;
}

:deep(.vue-advanced-cropper__line--horizontal) {
  height: 1px;
}

:deep(.vue-advanced-cropper__line--vertical) {
  width: 1px;
}

:deep(.vue-advanced-cropper__preview) {
  border-radius: 8px;
}
</style>
